/**
 * Stand-in for a custom key metric value. Using this requires a label and
 * value be provided to the visualization.
 */
export const CUSTOM_KEY_METRIC = Symbol('Custom key metric set. Value and label must be specified');

/**
 * Stand-in value for whatever key metric is currently used by default when
 * nothing is specified.
 */
export const DEFAULT_KEY_METRIC = Symbol('No key metric set. Use default');

/**
 * The key metric for the documents being displayed.
 *
 * Setting this ensures it is displayed with the document; if is not already
 * shown, it will replace the "reach" value in the bottom left corner of the
 * display.
 *
 * It often makes sense to pair this with the sort field being used to retrieve
 * documents.
 *
 * This value is meant, whenever possible, to mirror the relevant measure or
 * dimension names/values of universal analytics.
 *
 * @enum
 */
const KeyMetric = {
    /**
     * The estimated audience size for broadcast content
     * i.e. broadcast-specific reach
     */
    broadcastViewership: 'broadcast-viewership',

    /**
     * The estimated circulation of editorial/news content
     * i.e. editorial-specific reach
     */
    editorialReach: 'editorial-reach',

    /**
     * The sum of all types of social engagement with a document i.e. shares,
     * reactions and comments.
     */
    engagement: 'engagement',

    /**
     * The language in which the document is written
     */
    language: 'language',

    /**
     * The country from where the content originates
     */
    locationCountry: 'location-country',

    /**
     * Newsguard trust rating score for news sites
     */
    newsGuardScore: 'news-guard-score',

    /**
     * The estimated audience size for a document regardless of its medium
     */
    reach: 'reach',

    /**
     * The overall sentiment associated with the document
     */
    sentiment: 'sentiment',

    /**
     * Engagement value specific to editorial content
     */
    socialEcho: 'social-echo',

    /**
     * The estimated audidence of social content
     * i.e. social-specific reach
     */
    socialReach: 'social-reach',

    /**
     * Estimated or total actual views of the document.
     * Actual counts are only provided for some channels (see Information Model
     * documentation on Backstage for the list)
     */
    estimatedViews: 'estimated-views'
};
Object.freeze(KeyMetric);
export { KeyMetric };

/**
 * Test whether a string is a KeyMetric value
 * @name isValidKeyMetric
 *
 * @param {string|CUSTOM_KEY_METRIC|DEFAULT_KEY_METRIC} keyMetric - A string value which could equal a KeyMetric value
 *
 * @returns {boolean} True if the parameter equals a KeyMetric value or a constant which stands in for one. otherwise false.
 */
export function isValid(keyMetric) {
    return keyMetric === DEFAULT_KEY_METRIC
        || keyMetric === CUSTOM_KEY_METRIC
        || Object.values(KeyMetric).includes(keyMetric);
}

